import React, { useState } from 'react';
import { 
  XMarkIcon, 
  ShieldCheckIcon, 
  CheckIcon, 
  BoltIcon, 
  SparklesIcon, 
  ArrowRightIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

function PricingModal({ isOpen, onClose }) {
  const [billingCycle, setBillingCycle] = useState('annual'); // 'monthly' | 'annual'
  const [selectedTier, setSelectedTier] = useState('pro');
  const [procurementStatus, setProcurementStatus] = useState(null);

  if (!isOpen) return null;

  const tiers = [
    {
      id: 'developer',
      name: 'Developer & CI/CD',
      badge: 'MLOps Gate',
      priceMonthly: 499,
      priceAnnual: 399,
      description: 'Automated pre-flight gatekeeper and edge reflex defense for fast-moving engineering teams.',
      features: [
        'Up to 5 Registered AI Systems',
        'CI/CD Pre-Flight Gate (GitHub / GitLab)',
        'Jev System 1 Reflex Edge (<0.8ms)',
        'OWASP LLM01 Injection Shield',
        'GPAI FLOPs Systemic Calculator',
        'Standard Community Support'
      ],
      cta: 'Select Developer Tier',
      highlighted: false
    },
    {
      id: 'pro',
      name: 'DPO & Compliance Pro',
      badge: 'Most Popular',
      priceMonthly: 2500,
      priceAnnual: 1999,
      description: 'Comprehensive statutory compliance, FRIA audits, and cryptographic seals for risk teams.',
      features: [
        'Up to 25 Registered AI Systems',
        'Full EU AI Act Art. 27 FRIA Wizard',
        'Colorado SB 24-205 Discrimination Auditing',
        'HMAC-SHA256 Cryptographic Seals',
        '5 Legal & DPO Seat Licenses',
        'Closed-Loop Gap-to-Guardrail Remediation',
        'Priority 4-hour SLA Support'
      ],
      cta: 'Deploy Compliance Pro',
      highlighted: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise Board Defense',
      badge: 'Statutory Shield',
      priceMonthly: 8500,
      priceAnnual: 6999,
      description: 'Full statutory fine defense, unlimited models, board audit dossiers, and cloud marketplace billing.',
      features: [
        'Unlimited AI Models & Multi-Tenancy (RLS)',
        '1-Click Board Audit Dossier (PDF)',
        '€35M Statutory Penalty Exposure Protection',
        'Zero-Knowledge Reversible PII Vault',
        'GCP & AWS Marketplace Integration',
        'Dedicated Legal Escrow & 24/7 SLA',
        'On-Premises / Private VPC Option'
      ],
      cta: 'Contact Enterprise Sales',
      highlighted: false
    }
  ];

  const handleSimulateProcurement = (tier) => {
    setSelectedTier(tier.id);
    const amount = billingCycle === 'annual' ? tier.priceAnnual * 12 : tier.priceMonthly;
    setProcurementStatus({
      tierName: tier.name,
      amount,
      cycle: billingCycle,
      orderId: `OPS-GCP-${Math.floor(100000 + Math.random() * 900000)}`,
      date: new Date().toISOString().split('T')[0]
    });
  };

  const downloadQuotePDF = () => {
    const quoteText = `
OPSIGHT AI ENTERPRISE LICENSING QUOTE
Reference Order: ${procurementStatus?.orderId || 'OPS-QUOTE-2026'}
Date: ${new Date().toISOString().split('T')[0]}
Plan: ${tiers.find(t => t.id === selectedTier)?.name} (${billingCycle.toUpperCase()} Billing)
Total Investment: $${billingCycle === 'annual' ? (tiers.find(t => t.id === selectedTier)?.priceAnnual * 12).toLocaleString() : tiers.find(t => t.id === selectedTier)?.priceMonthly.toLocaleString()} / year
Statutory Liability Protection: EU AI Act (€35,000,000 maximum penalty) & Colorado SB 24-205 ($20,000 / civil infraction).
Procurement Mechanism: Google Cloud Marketplace or Direct Invoice (Net 30).
`.trim();

    const blob = new Blob([quoteText], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `OpsSight-AI-Procurement-Quote-${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full p-6 sm:p-8 relative border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        {/* Modal Header */}
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold uppercase tracking-wider mb-3">
            <SparklesIcon className="w-4 h-4 text-blue-600" />
            Commercial Licensing & Fine Protection
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Enterprise AI Governance & Regulatory Shield
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-2">
            Insure your organization against <strong className="text-slate-900">€35,000,000 (7% turnover)</strong> EU AI Act statutory fines and Colorado SB 24-205 algorithmic bias exposure with sub-millisecond automated enforcement.
          </p>

          {/* Statutory ROI Banner */}
          <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 via-teal-50 to-blue-50 border border-emerald-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between text-xs gap-2">
            <div className="flex items-center gap-2 text-emerald-900">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span><strong>Statutory Protection Ratio:</strong> Single Article 5 fine exceeds platform cost by <strong>350x</strong>.</span>
            </div>
            <span className="font-mono text-emerald-700 font-bold bg-white px-2 py-0.5 rounded-lg border border-emerald-200">
              99.7% Risk Mitigation ROI
            </span>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="mt-6 inline-flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              Monthly Billing
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                billingCycle === 'annual'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <span>Annual Commitment</span>
              <span className="bg-emerald-500 text-white text-[10px] px-1.5 py-0.2 rounded-full uppercase tracking-wider font-extrabold">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Procurement Simulator Feedback Banner */}
        {procurementStatus && (
          <div className="mb-6 p-4 rounded-2xl bg-slate-900 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-lg">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                  Procurement Simulation Initiated
                </span>
                <span className="text-slate-400 text-xs font-mono">[{procurementStatus.orderId}]</span>
              </div>
              <p className="text-xs text-slate-300 mt-1">
                Selected Plan: <strong>{procurementStatus.tierName}</strong> &bull; Total: <strong>${procurementStatus.amount.toLocaleString()}</strong> ({procurementStatus.cycle}). Pre-approved for Google Cloud Marketplace drawdown.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadQuotePDF}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold border border-slate-700"
              >
                <DocumentArrowDownIcon className="w-4 h-4 text-blue-400" />
                Download Quote
              </button>
              <button
                onClick={() => setProcurementStatus(null)}
                className="text-xs text-slate-400 hover:text-white px-2 py-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* 3 Tier Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const price = billingCycle === 'annual' ? tier.priceAnnual : tier.priceMonthly;
            const isSelected = selectedTier === tier.id;

            return (
              <div
                key={tier.id}
                className={`rounded-2xl p-6 transition-all relative flex flex-col justify-between ${
                  isSelected
                    ? 'border-2 border-blue-600 shadow-xl bg-gradient-to-b from-blue-50/40 via-white to-white ring-2 ring-blue-500/20'
                    : tier.highlighted
                    ? 'border-2 border-indigo-500 shadow-lg bg-gradient-to-b from-indigo-50/30 via-white to-white'
                    : 'border border-slate-200 bg-white shadow-xs hover:border-slate-300'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-black uppercase tracking-wider shadow-sm">
                    {tier.badge}
                  </div>
                )}

                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-black text-slate-900 tracking-tight">{tier.name}</h3>
                      <p className="text-[11px] text-slate-500 mt-1 leading-snug">{tier.description}</p>
                    </div>
                  </div>

                  <div className="my-5 pb-5 border-b border-slate-100">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        ${price.toLocaleString()}
                      </span>
                      <span className="text-xs text-slate-500 font-medium">/ month</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="text-[10px] text-emerald-600 font-semibold mt-1">
                        Billed annually (${(price * 12).toLocaleString()}/year)
                      </div>
                    )}
                  </div>

                  {/* Feature Checklist */}
                  <div className="space-y-2.5 mb-6">
                    <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                      Included Capabilities:
                    </div>
                    {tier.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-slate-700">
                        <CheckIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Call to Action Button */}
                <button
                  onClick={() => handleSimulateProcurement(tier)}
                  className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold tracking-wide transition-all flex items-center justify-center gap-1.5 ${
                    tier.highlighted
                      ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-500/20'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                  }`}
                >
                  <span>{tier.cta}</span>
                  <ArrowRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer Guarantee */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-3">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
              SOC 2 Type II Certified Architecture
            </span>
            <span className="flex items-center gap-1">
              <BoltIcon className="w-4 h-4 text-emerald-600" />
              Google Cloud Marketplace Drawdown Eligible
            </span>
          </div>
          <span className="text-[11px] text-slate-400">Net 30 Invoicing & Custom Enterprise MSAs available</span>
        </div>
      </div>
    </div>
  );
}

export default PricingModal;
